import { CommonModule } from '@angular/common';
import { Component, ViewChild, ElementRef, AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { FloatingAssistanceButtonComponent } from '../../../shared/components/floating-assistance-button/floating-assistance-button.component';
import { MatIcon } from '@angular/material/icon';
import { LoaderService } from '../../../shared/services/loader.service';
import { Router, ActivatedRoute } from '@angular/router';
import { ServicesService } from '../../services/services/services.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { MatDialog } from '@angular/material/dialog';
import { NotificationModalComponent } from '../../../shared/components/notification-modal/notification-modal.component';


@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    FloatingAssistanceButtonComponent,
    MatIcon,
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {

  popularServices: any[] = [];
  coberturaServices: any[] = [];
  showPopularServices = true;

  formatUsage(tomados: string, disponibles: string): string {
    const taken = Number(tomados);
    const available = disponibles === '00' ? '∞' : disponibles;
    return `${taken}/${available}`;
  }

  servicesNames: string[] = [];
  tabs: any[] = [];

  private loadedImages = 0;
  private totalImages = 0;

  currentCoberturaIndex = 0;
  @ViewChild('coberturaContainer') coberturaContainer!: ElementRef<HTMLElement>;

  constructor(
    private servicesService: ServicesService,
    private loader: LoaderService,
    private notification: NotificationService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadServices();
    
    // Si hay flag de reload, recargar la página completa
    if (localStorage.getItem('needsPageReload')) {
      localStorage.removeItem('needsPageReload');
      window.location.reload();
    }
  }

  ngOnDestroy(): void {}

  ngAfterViewInit(): void {}

  // -----------------------------------------
  // Cargar servicios
  // -----------------------------------------
  loadServices(): void {
    this.loader.show();
    this.servicesService.getServicesPopular().subscribe({
      next: (response) => {
        if (response.code === 200) {
          const processedData = this.normalizeServiceNames(response.data);
          this.popularServices = processedData;
          this.coberturaServices = processedData;
          this.processServicesData();
        } else {
          this.loader.hide();
          this.notification.showError('No se pudieron cargar los servicios.');
        }
      },
      error: () => {
        this.loader.hide();
        this.notification.showError('Hubo un problema al cargar los servicios.');
      }
    });
  }

  private processServicesData(): void {
    this.servicesNames = [...new Set(this.popularServices.map(s => s.dsServicio))];

    this.tabs = this.servicesNames.map(tipo => ({
      label: tipo.replace(/Asistencia\s*(en)?/g, '').trim(),
      icon: this.iconForTipo(tipo.replace(/Asistencia\s*(en)?/g, '').trim())
    }));

    this.createServicesContact();
    this.preloadImages();
  }

  private iconForTipo(tipo: string) {
    switch (tipo) {
      case 'Médica': return 'medical_services';
      case 'Vial': return 'directions_car';
      case 'Hogar': return 'home';
      case 'Mascotas': return 'pets';
      case 'Legal': return 'balance';
      case 'Psicologica': return 'psychology';
      case 'Nutricional': return 'restaurant_menu';
      case 'Viajes Nacional': return 'flight';
      default: return 'help_outline';
    }
  }

  private createServicesContact(): void {
    const uniqueServices = this.popularServices.filter(
      (service, index, self) =>
        index === self.findIndex(s => s.clSubServicio === service.clSubServicio)
    );

    const servicesContact = uniqueServices.map(service => ({
      clSubServicio: service.clSubServicio,
      phone: service.phone || '',
      whatsapp: service.whatsapp || ''
    }));

    localStorage.setItem('servicesContact', JSON.stringify(servicesContact));
  }

  // -----------------------------------------
  // Validación de límite de servicios 
  // -----------------------------------------
  goToSubService(service: any) {
    const tomados = Number(service.Tomados);
    const disponibles = service.Disponibles === '00' ? Infinity : Number(service.Disponibles);

    if (tomados >= disponibles && disponibles !== Infinity) {
      this.dialog.open(NotificationModalComponent, {
        data: {
          type: 'info',
          title: 'Has alcanzado el límite de servicios disponibles.',
          message: 'Si tienes servicios en curso, puedes consultar su detalle en el apartado "<b>Solicitudes</b>".'
        }
      });
      return;
    }

    // Guardar solicitudData con el objeto service completo
    const solicitudData = {
      servicio: {
        clServicio: service.clServicio,
        clSubservicio: service.clSubServicio,
        dsServicio: service.dsServicio,
        dsSubServicio: service.dsSubServicio
      }
    };
    localStorage.setItem('solicitudData', JSON.stringify(solicitudData));

    // Si aún tiene disponibles, sí puede entrar
    this.router.navigate(
      ['/service', service.clSubServicio],
      { state: service }
    );
  }

  // -----------------------------------------
  // Navegar a lista completa
  // -----------------------------------------
  goToServices(name: string) {
    this.router.navigate(['/services'], {
      queryParams: { services: name }
    });
  }

  // -----------------------------------------
  // Normalización útil
  // -----------------------------------------
  private normalizeServiceNames(services: any[]): any[] {
    const serviceNameMap: { [key: string]: string } = {
      '433': 'Orientación Médica Online',
      '383': 'Consulta Médica en consultorio',
      '222': 'Handyman',
      '468': 'Deep cleaning',
      '232': 'Concierge',
      '286': 'Asistencia Legal'
    };

    return services.map(service => ({
      ...service,
      dsSubServicio: serviceNameMap[service.clSubServicio] || service.dsSubServicio
    }));
  }

  // -----------------------------------------
  // Preload imágenes
  // -----------------------------------------
  private preloadImages(): void {
    const imageUrls = this.popularServices.filter(s => s.img).map(s => s.img);

    this.totalImages = imageUrls.length;
    this.loadedImages = 0;

    if (this.totalImages === 0) {
      setTimeout(() => this.loader.hide(), 0);
      return;
    }

    imageUrls.forEach(url => {
      const img = new Image();
      img.onload = () => this.onImageLoad();
      img.onerror = () => this.onImageLoad();
      img.src = url;
    });
  }

  private onImageLoad(): void {
    this.loadedImages++;
    if (this.loadedImages >= this.totalImages) {
      this.loader.hide();
    }
  }

  // -----------------------------------------
  // Scroll horizontal
  // -----------------------------------------
  scrollCoberturaLeft(): void {
    if (this.currentCoberturaIndex > 0) {
      this.currentCoberturaIndex--;
      this.scrollToCobertura();
    }
  }

  scrollCoberturaRight(): void {
    const maxIndex = this.coberturaServices.length - 1;
    if (this.currentCoberturaIndex < maxIndex) {
      this.currentCoberturaIndex++;
      this.scrollToCobertura();
    }
  }

  private scrollToCobertura(): void {
    if (!this.coberturaContainer) return;

    const cardWidth = 342;
    this.coberturaContainer.nativeElement.scrollTo({
      left: this.currentCoberturaIndex * cardWidth,
      behavior: 'smooth'
    });
  }

  onCoberturaScroll(event: Event): void {
    const container = event.target as HTMLElement;
    const cardWidth = 342;
    this.currentCoberturaIndex = Math.round(container.scrollLeft / cardWidth);
  }

  get canScrollCoberturaLeft(): boolean {
    return this.currentCoberturaIndex > 0;
  }

  get canScrollCoberturaRight(): boolean {
    return this.currentCoberturaIndex < this.coberturaServices.length - 1;
  }
}
